// @ts-nocheck — the landing's behaviour, kept as the reviewed 1.3″ prototype wrote it (vanilla DOM over static markup).
// mountLanding(root) runs it inside root and returns a cleanup that stops every timer, frame, observer and
// window listener it started and removes what it added to the DOM, so a remount (StrictMode, navigating back) starts clean.
//
//   decrypt    section kickers resolve from cipher as they scroll in; the hero's "Feels local." on load
//   figures    uptime bars, meter cells, hover gestures (uptime, desktop), the chip's tilt
//   pricing    the machine drawing (PZ13); the picker itself is React (Sections.tsx Pricing)
//   footer     `cmx --help` types, the columns print, then the dot-matrix cmx_ wordmark types (hover: the dots light up, click: decrypt)

/* cmx 1.3″ pricing: the machine you're building, drawn from the parts you pick.
 *
 *   PZ13.draw(svg)            builds the drawing once (every part, filled and empty)
 *   PZ13.set(svg, {cpu, mem, sto})   a size (Standard S 2/8/100, M 4/16/200, L 8/32/400, XL 16/32/400): 2, 4, 8 or 16 modules drop in / lift out,
 *                                    their labels read what they hold, a drive lights per 100 GB
 *
 *   The chassis is the base: its front carries four drive bays (one lit per 100 GB) and the power light.
 *   On top, four slots in a 2×2: the back row holds the CPU modules (4 core = one, 8 core = two),
 *   the front row the memory (16 GB = one, 32 GB = two). An empty slot shows its footprint and screw holes.
 *   XL is L stacked twice: a second chassis rises in underneath and a second layer of modules drops onto the first,
 *   so the drawing grows about as much up as down and keeps its viewBox (S, M and L are drawn exactly as before).
 *   2:1 pixel isometric: P(x, y, z) = [x − y, (x + y) / 2 − z]; x runs right-down, y left-down, z up. */
export const PZ13 = (function () {
  const C = { line: '#8A8A8A', soft: '#BDBDBD', top: '#FFFFFF', left: '#F7F7F7', right: '#EDEDED', vent: '#EBEBEB', screw: '#1C1917', label: '#A6A09B', blue: '#1570EF', tint: '#EFF8FF', tintLine: '#84CAFF', hole: '#D4D4D4' };
  // XL's added layers (the lower chassis, the upper modules) in the blue scheme, so the doubling reads at a glance
  const B = { ...C, line: '#53B1FD', soft: '#84CAFF', top: '#F5FAFF', left: '#EFF8FF', right: '#D1E9FF', vent: '#F5FAFF', screw: '#1570EF', label: '#2E90FA' };
  const NS = ' vector-effect="non-scaling-stroke"', f2 = n => (+n).toFixed(2);
  const P = (x, y, z = 0) => [x - y, (x + y) / 2 - z];
  const pts = a => a.map(p => f2(p[0]) + ',' + f2(p[1])).join(' ');
  const poly = (a, fill, o = {}) => `<polygon points="${pts(a)}" fill="${fill}" stroke="${o.stroke || C.line}" stroke-width="1" stroke-linejoin="round"${o.dash ? ` stroke-dasharray="${o.dash}"` : ''}${NS}/>`;
  const mat = (p, a, b, c, d) => `matrix(${a},${b},${c},${d},${f2(p[0])},${f2(p[1])})`;
  // a box: its three visible faces, and the matrices that put flat detail on them (u along the face, v down / across)
  function box(x, y, z, w, d, h, o = C) {
    const k = { stroke: o.line };
    const s = poly([P(x, y + d, z + h), P(x + w, y + d, z + h), P(x + w, y + d, z), P(x, y + d, z)], o.left, k) +
      poly([P(x + w, y + d, z + h), P(x + w, y, z + h), P(x + w, y, z), P(x + w, y + d, z)], o.right, k) +
      poly([P(x, y, z + h), P(x + w, y, z + h), P(x + w, y + d, z + h), P(x, y + d, z + h)], o.top, k);
    return { s, L: mat(P(x, y + d, z + h), 1, .5, 0, 1), R: mat(P(x + w, y + d, z + h), 1, -.5, 0, 1), T: mat(P(x, y, z + h), 1, .5, -1, .5) };
  }
  const on = (m, inner) => `<g transform="${m}">${inner}</g>`;
  const vents = (u, v, n, k = C, gap = 6, w = 2.6, h = 11) => Array.from({ length: n }, (_, i) => `<rect x="${f2(u + i * gap)}" y="${v}" width="${w}" height="${h}" rx="1.3" fill="${k.vent}" stroke="${k.soft}" stroke-width="1"${NS}/>`).join('');
  const screws = (w, d, i, k = C) => [[i, i], [w - i, i], [w - i, d - i], [i, d - i]].map(([u, v]) => `<circle cx="${u}" cy="${v}" r="1.6" fill="${k.screw}"/>`).join('');
  const holes = (w, d, i) => [[i, i], [w - i, i], [w - i, d - i], [i, d - i]].map(([u, v]) => `<circle cx="${u}" cy="${v}" r="1.6" fill="none" stroke="${C.soft}" stroke-width="1"${NS}/>`).join('');
  const label = (u, v, t, k = C, size = 7) => `<text x="${u}" y="${v}" font-family="Geist Mono, ui-monospace, monospace" font-size="${size}" font-weight="500" letter-spacing=".9" fill="${k.label}">${t}</text>`;

  // the frame: chassis 376 × 196 × 34, four columns of two slots (88 × 94, 8 gaps), modules 26 tall
  const COLS = 4, CW = 376, CD = 196, CH = 34, G = 8, MW = (CW - (COLS - 1) * G) / COLS, MD = (CD - G) / 2, MH = 26;
  const LIFT = 4;   // a module rests 4 above the chassis, so the stack reads as trays
  // a column is a pair, 2 core at the back and 8 GB in front: a module per core, so S fills one column, M two, L all four.
  // Back row first, so later slots paint over earlier ones
  const SLOTS = [0, 1].flatMap(r => [0, 1, 2, 3].map(c => ({ id: (r ? 'mem' : 'cpu') + c, col: c, x: c * (MW + G), y: r * (MD + G), kind: r ? 'MEM' : 'CPU', val: '' })));
  // XL's second layer, painted after the first: the same slots, a module's height (and a lift) higher
  const LAYERS = [0, 1].flatMap(l => SLOTS.map(s => ({ ...s, layer: l, z: CH + LIFT + l * (MH + LIFT) })));

  function chassis(z = 0, unit = 0) {
    const k = unit ? B : C, b = box(0, 0, z, CW, CD, CH, k);
    // the front: four drive bays (a tray, a pull line, its light) then the vents and the power light
    const bay = i => { const u = 10 + i * 44; return `<g class="pz-bay" data-i="${unit * 4 + i}"><rect class="pz-tray" x="${u}" y="9" width="38" height="16" rx="2.5" fill="#FFFFFF" stroke="${k.line}" stroke-width="1"${NS}/>` +
      `<path d="M${u + 6} 15.5H${u + 22}" stroke="${k.soft}" stroke-width="1"${NS}/><circle class="pz-led" cx="${u + 31}" cy="19" r="1.9" fill="${C.hole}"/></g>`; };
    const front = [0, 1, 2, 3].map(bay).join('') + vents(190, 11, Math.floor((CW - 222) / 6), k) + `<circle class="pz-pwr" cx="${CW - 14}" cy="${CH - 9}" r="1.9" fill="${C.blue}"/>`;
    const side = vents(14, 11, 24, k);
    // XL's lower unit: its top is covered by the unit above, so it carries no slots
    if (unit) return `<g class="pz-base2">${b.s}${on(b.L, front)}${on(b.R, side)}</g>`;
    return b.s + on(b.L, front) + on(b.R, side) + on(b.T, SLOTS.map(s => `<g class="pz-slot" data-col="${s.col}" transform="translate(${f2(s.x)} ${f2(s.y)})"><rect x="1.5" y="1.5" width="${f2(MW - 3)}" height="${f2(MD - 3)}" rx="3" fill="none" stroke="${C.soft}" stroke-width="1" stroke-dasharray="3 3"${NS}/>${holes(MW, MD, 9)}</g>`).join(''));
  }
  function module(s) {
    const k = s.layer ? B : C, b = box(s.x, s.y, s.z, MW, MD, MH, k);
    const right = vents(10, 8, 12, k);
    const top = screws(MW, MD, 9, k) + label(14, MD - 16, `${s.kind} · ${s.val}`, k).replace('<text ', '<text class="pz-lbl" ');
    return `<g class="pz-mod" data-slot="${s.id}" data-col="${s.layer * COLS + s.col}">${b.s}${on(b.R, right)}${on(b.T, top)}</g>`;
  }

  const PZ13 = {
    draw(svg) {
      // bounds of the whole drawing (chassis + modules), so the viewBox frames it with even air
      const xs = [P(0, CD)[0], P(CW, 0)[0]], ys = [P(0, 0, CH + LIFT + MH)[1], P(CW, CD, 0)[1]];
      const pad = 2, vb = [xs[0] - pad, ys[0] - pad, xs[1] - xs[0] + 2 * pad, ys[1] - ys[0] + 2 * pad];
      svg.setAttribute('viewBox', vb.map(f2).join(' '));
      svg.innerHTML = chassis(-(CH + LIFT), 1) + chassis() + LAYERS.map(module).join('');
      svg.dataset.drawn = '1';
    },
    set(svg, { cpu = 4, mem = 16, sto = 100 } = {}) {
      if (!svg.dataset.drawn) PZ13.draw(svg);
      // one 2 core + 8 GB pair per column: S (2 / 8) fills one, M (4 / 16) two, L (8 / 32) all four, XL (16 / 32) both layers
      const n = Math.min(2 * COLS, Math.max(1, Math.round(cpu / 2)));
      // the parts that change, staggered by stack level (0 the lower chassis, 1 and 2 the module layers):
      // what arrives settles bottom-up, what leaves lifts off top-down. Unchanged parts lose any old delay
      const flip = [];
      const turn = (g, v, lv) => { if (g.classList.contains('on') !== v) flip.push([g, v, lv]); else g.style.setProperty('--d', '0ms'); };
      const mods = [...svg.querySelectorAll('.pz-mod')], base2 = svg.querySelector('.pz-base2');
      mods.forEach(g => turn(g, +g.dataset.col < n, +g.dataset.col < COLS ? 1 : 2));
      turn(base2, n > COLS, 0);
      [true, false].forEach(v => {
        const lv = [...new Set(flip.filter(f => f[1] === v).map(f => f[2]))].sort((a, b) => v ? a - b : b - a);
        flip.forEach(([g, w, l]) => { if (w === v) g.style.setProperty('--d', `${lv.indexOf(l) * 50}ms`); });
      });
      mods.forEach(g => {
        const c = +g.dataset.col, cpuSlot = g.dataset.slot.startsWith('cpu'), t = g.querySelector('.pz-lbl');
        g.classList.toggle('on', c < n);
        if (t && c < n) t.textContent = cpuSlot ? `CPU · ${cpu / n} CORE` : `MEM · ${mem / n} GB`;
      });
      base2.classList.toggle('on', n > COLS);
      // a filled slot drops its dashed outline and holes, in step with its module (fading as it lands, back as it lifts)
      svg.querySelectorAll('.pz-slot').forEach(g => {
        const m = svg.querySelector(`.pz-mod[data-col="${g.dataset.col}"]`);
        g.style.setProperty('--d', m.style.getPropertyValue('--d') || '0ms');
        g.classList.toggle('full', +g.dataset.col < n);
      });
      const lit = Math.min(8, Math.max(1, Math.round(sto / 100)));
      svg.querySelectorAll('.pz-bay').forEach(g => g.classList.toggle('on', +g.dataset.i < lit));
    },
  };
  return PZ13;
})();

export function mountLanding(root: HTMLElement): () => void {
  let alive = true;
  const bin = new Set();
  const track = (fn) => { bin.add(fn); return fn; };
  // the prototype's globals, shadowed so everything it starts is tracked
  const addEventListener = (t, f, o) => { window.addEventListener(t, f, o); track(() => window.removeEventListener(t, f, o)); };
  const setTimeout = (f, ms, ...a) => { const id = window.setTimeout(() => { if (alive) f(...a); }, ms); track(() => window.clearTimeout(id)); return id; };
  const setInterval = (f, ms) => { const id = window.setInterval(() => { if (alive) f(); }, ms); track(() => window.clearInterval(id)); return id; };
  const requestAnimationFrame = (f) => window.requestAnimationFrame((t) => { if (alive) f(t); });
  class IntersectionObserver extends window.IntersectionObserver { constructor(cb, o) { super(cb, o); track(() => this.disconnect()); } }
  class MutationObserver extends window.MutationObserver { constructor(cb) { super(cb); track(() => this.disconnect()); } }
  const document = window.document;
  // what the scripts add to the static markup, taken back out on cleanup
  const kickers = [...root.querySelectorAll('[data-dc]')].map((el) => [el, el.textContent]);
  const hl = root.querySelector('.hl-t'); const hlText = hl && hl.textContent;
  track(() => {
    kickers.forEach(([el, t]) => { el.classList.remove('dc'); el.textContent = t; });
    if (hl) { hl.classList.remove('dc'); hl.textContent = hlText; }
    root.querySelectorAll('#days, .cells[data-cells]').forEach((e) => e.replaceChildren());
    const pz = root.querySelector('#pzSvg'); if (pz) { pz.replaceChildren(); delete pz.dataset.drawn; }
    const f = root.querySelector('footer.foot'); if (f) { f.replaceChildren(); f.className = 'foot'; }
    root.querySelectorAll('[data-arm], [data-in]').forEach((e) => { e.removeAttribute('data-arm'); e.removeAttribute('data-in'); });
  });

  (() => {
const TIMING = { decrypt: 360 };
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const hoverable = matchMedia('(hover: hover)').matches;
const $ = (s, r = root) => r.querySelector(s);
const $$ = (s, r = root) => [...r.querySelectorAll(s)];
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
/* ---------------------------------------------------------------- decrypt */
const CIPHER = '#*+=<>/\\%$&01XZAS';
function decrypt(el, delay = 0, dur = 460) {
  if (reduce || !el) return Promise.resolve();
  const text = el.textContent;
  el.classList.add('dc');
  el.textContent = '';
  const ghost = document.createElement('span'); ghost.className = 'dc-ghost'; ghost.textContent = text;
  const live = document.createElement('span'); live.className = 'dc-live'; live.setAttribute('aria-hidden', 'true');
  el.append(ghost, live);
  const scr = (from) => { let s = text.slice(0, from); for (let j = from; j < text.length; j++) s += /[\s\-\/.]/.test(text[j]) ? text[j] : CIPHER[(Math.random() * CIPHER.length) | 0]; return s; };
  live.textContent = scr(0);
  return new Promise((res) => {
    const t0 = performance.now();
    const step = () => {
      const p = (performance.now() - t0 - delay) / dur;
      if (p >= 1) { el.classList.remove('dc'); el.textContent = text; res(); return; }
      live.textContent = scr(p <= 0 ? 0 : Math.floor((1 - (1 - p) * (1 - p)) * text.length));
      setTimeout(step, 46);
    };
    setTimeout(step, 46);
  });
}

/* hero highlight: the phrase decrypts inside it */
if (!reduce) decrypt($('.hl-t'), TIMING.decrypt);

/* section kickers decrypt as they enter (text is readable at rest) */
if (!reduce && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); decrypt(e.target, 80); } }), { rootMargin: '0px 0px -20% 0px' });
  $$('[data-dc]').forEach((el) => io.observe(el));
}


/* ------------------------------------------------------------ figures */
/* uptime: 30 full days */
const days = $('#days');
for (let i = 0; i < 30; i++) days.appendChild(document.createElement('i'));
/* meters: n cells, k lit */
$$('.cells[data-cells]').forEach((c) => { const n = +c.dataset.cells, k = +c.dataset.on; for (let i = 0; i < n; i++) { const e = document.createElement('i'); if (i < k) e.className = 'on'; c.appendChild(e); } });

/* Each panel runs a small gesture only while its card is hovered (or, on
   touch, while it is on screen). Poses drive CSS transitions, so leaving
   mid-gesture glides home instead of snapping. */
function beats(name) {
  return {
    ms: 620, k: 0, el: $(`[data-loop="${name}"]`),
    step() { const s = [0, 1, 2, 3, null, null][this.k]; s === null ? this.el.removeAttribute('data-s') : this.el.setAttribute('data-s', s); this.k = (this.k + 1) % 6; },
    rest() { this.k = 0; this.el.removeAttribute('data-s'); }
  };
}
const loops = {
  up: {
    ms: 90, k: 0, bars: () => $$('i', days),
    step() { const b = this.bars(); b.forEach((e, i) => { e.style.transform = i === this.k ? 'scaleY(.82)' : ''; }); this.k = (this.k + 1) % b.length; },
    rest() { this.k = 0; this.bars().forEach((e) => { e.style.transform = ''; }); }
  },
  dt: {
    ms: 900, k: 0, el: $('#dt'),
    pts: () => { const w = $('.dt-win', $('#dt')).clientWidth; return [[w * .5, 44], [w - 34, 70], [w * .2, 34], [w * .62, 22]]; },
    step() { const pts = this.pts(); this.k = (this.k + 1) % pts.length; const [x, y] = pts[this.k]; const p = $('.dt-ptr', this.el);
      p.style.setProperty('--px', x + 'px'); p.style.setProperty('--py', y + 'px');
      clearTimeout(this.t); this.el.removeAttribute('data-click');
      this.t = setTimeout(() => { this.el.setAttribute('data-click', ''); if (this.k === 1) { const b = $('.dt-btn', this.el); b.style.transform = 'scale(.94)'; setTimeout(() => (b.style.transform = ''), 140); } }, 600); },
    rest() { clearTimeout(this.t); this.k = 0; const p = $('.dt-ptr', this.el); const [x, y] = this.pts()[0]; p.style.setProperty('--px', x + 'px'); p.style.setProperty('--py', y + 'px'); this.el.removeAttribute('data-click'); }
  },
  chip: { ms: 0, step() {}, rest() {} },
  // four beats then a hold of two; CSS draws each beat from data-s, and no data-s is the finished frame:
  // localhost: the command types, its link prints, it opens on this Mac
  loc: beats('loc'),
  // clipboard: copied on this Mac, pasted on cmx-you, then what was copied
  clip: beats('clip'),
  // drag and drop: the screenshot is dragged in, lands in the prompt, then Finder's confirmation
  drop: beats('drop'),
  // sign in: the command types, the device link prints, it opens in your browser
  auth: beats('auth'),
  // after a restart: the sessions come back one by one
  restart: beats('restart'),
  // preinstalled: the tools appear one after another, like an install finishing
  pre: beats('pre'),
  // this Mac: with the heavy work in the cloud, its cpu just idles a few percent either way
  cool: {
    ms: 700, k: 0, el: $('[data-loop="cool"]'),
    seq: [[2, '9%'], [1, '4%'], [1, '7%'], [2, '11%'], [1, '5%'], [1, '6%']],
    set(on, pct) { const m = $('.meter', this.el); $$('.cells i', m).forEach((c, i) => c.classList.toggle('on', i < on)); $('b', m).textContent = pct; },
    step() { const [on, pct] = this.seq[this.k]; this.set(on, pct); this.k = (this.k + 1) % this.seq.length; },
    rest() { this.k = 0; this.set(1, '6%'); }
  }
};
loops.dt.rest();
addEventListener('resize', () => loops.dt.rest());
$$('[data-loop]').forEach((card) => {
  const L = loops[card.dataset.loop]; let timer = 0;
  const start = () => { if (reduce || timer || !L.ms) return; L.step(); timer = setInterval(() => L.step(), L.ms); };
  const stop = () => { clearInterval(timer); timer = 0; L.rest(); };
  if (hoverable) { card.addEventListener('pointerenter', start); card.addEventListener('pointerleave', stop); }
  else if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), { threshold: 0.6 }).observe(card);
});

/* chip: weighted tilt + a specular that sits under the pointer */
const chip = $('#chip');
if (hoverable && !reduce) {
  const card = chip.closest('.cell');
  card.addEventListener('pointermove', (e) => {
    const r = chip.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width - .5, y = (e.clientY - r.top) / r.height - .5;
    chip.style.setProperty('--ry', (Math.max(-.8, Math.min(.8, x)) * 16).toFixed(2) + 'deg'); chip.style.setProperty('--rx', (Math.max(-.8, Math.min(.8, y)) * -16).toFixed(2) + 'deg');
    chip.style.setProperty('--sx', ((x + .5) * 100).toFixed(1) + '%'); chip.style.setProperty('--sy', ((y + .5) * 100).toFixed(1) + '%');
  });
  card.addEventListener('pointerenter', () => { chip.style.setProperty('--cs', 1.04); chip.style.setProperty('--so', 1); });
  card.addEventListener('pointerleave', () => { ['--rx', '--ry', '--cs', '--so'].forEach((p) => chip.style.removeProperty(p)); });
}

/* Magic Setup: every page that lands moves the copy on a project. The bar fills a block, the count and both paths tick to the next
   (the page's loop is CSS; this only listens for its iterations). Reduced motion keeps the resting frame: 9 of 15, ~/code/ui */
const cp = $('#setup .cp');
if (cp && !reduce) {
  const P = ['webapp', 'payments', 'api', 'admin', 'docs', 'mobile', 'cli', 'infra', 'ui', 'landing', 'search', 'auth', 'worker', 'e2e', 'scripts'];
  const px = $('.pg-x', cp), bar = $('.cpbar', cp), cnt = $('.cnt', cp), cur = $$('.cur', cp);
  const show = (n) => {
    $$('i', bar).forEach((c, k) => c.classList.toggle('on', k < n));
    bar.setAttribute('aria-valuenow', n); cnt.textContent = n; cur.forEach((c) => (c.textContent = '~/code/' + P[n - 1]));
  };
  let n = 9;
  const tick = () => { n = n % 15 + 1; show(n); };
  px.addEventListener('animationiteration', tick);
  track(() => { px.removeEventListener('animationiteration', tick); show(9); });
}

/* your work, multiplied, told laptop first: once the meters are in view the laptop's wheels roll to 0008 and its two lines
   print (CSS), then at 2 s cmx's wheels roll to 0384 (CSS) and its log prints its first five lines. After that it prints
   like a terminal, one line at a time: it types (1 s), its hours load (spinner, 300 ms), 24 h lands and holds 500 ms, and
   only then does the next line print; once the log is full the oldest line scrolls off the top. It runs only while the section is on screen; reduced motion shows the finished frame */
const hm = $('#multiply .hm');
if (hm && !reduce && 'IntersectionObserver' in window) {
  hm.setAttribute('data-arm', '');
  const log = $('.hm-cmx .hm-lr', hm), order = [...log.children];
  order.slice(0, 5).forEach((li) => li.classList.add('wait'));
  const timers = new Set();
  const later = (fn, ms) => { const id = window.setTimeout(() => { timers.delete(id); fn(); }, ms); timers.add(id); };
  const clear = () => { timers.forEach(clearTimeout); timers.clear(); };
  const print = (li) => { li.classList.remove('wait', 'nw'); void li.offsetWidth; li.classList.add('nw'); };
  // the next waiting line prints; when none is left, the oldest scrolls off and the bottom one prints
  const next = () => {
    $$('.nw', log).forEach((li) => li.classList.remove('nw'));
    const w = $('.wait', log);
    if (w) { print(w); return; }
    log.appendChild(log.firstElementChild); print(log.children[4]);
  };
  const run = () => later(() => { next(); run(); }, 1950);
  let started = false;
  const io = new IntersectionObserver(([e]) => {
    if (!e.isIntersecting) { clear(); return; }
    if (timers.size) return;
    if (!started) {
      started = true; hm.setAttribute('data-in', '');
      // the cmx wheels finish rolling at ~6.3 s; the log starts after them
      later(() => { next(); run(); }, 6400);
    } else run();
  }, { threshold: 0.35 });
  io.observe(hm);
  // put React's lines back in their order on the way out
  track(() => { io.disconnect(); clear(); order.forEach((li) => { li.classList.remove('nw', 'wait'); log.appendChild(li); }); hm.removeAttribute('data-arm'); hm.removeAttribute('data-in'); });
}

/* FAQ: an answer opens and closes by height (ease-in-out, the close a touch shorter). The <details> stays native, so it still
   toggles without script or with reduced motion. A click mid-way reverses from wherever the answer is. */
const IO = 'cubic-bezier(.65,0,.35,1)';
$$('.faq details').forEach((d) => {
  const s = $('summary', d), a = $('.fq-a', d);
  const click = (e) => {
    if (reduce) return;
    e.preventDefault();
    const closing = d.open && !d.hasAttribute('data-closing');
    const h0 = d.open ? a.getBoundingClientRect().height : 0, m0 = d.open ? getComputedStyle(a).marginTop : '0px', o0 = d.open ? getComputedStyle(a).opacity : '0';
    a.getAnimations().forEach((x) => x.cancel());
    if (closing) {
      d.setAttribute('data-closing', '');
      a.animate([{ height: h0 + 'px', marginTop: m0, opacity: o0 }, { height: '0px', marginTop: '0px', opacity: 0 }], { duration: 240, easing: IO, fill: 'forwards' })
        .onfinish = (ev) => { d.open = false; d.removeAttribute('data-closing'); (ev.target as Animation).cancel(); };
    } else {
      d.removeAttribute('data-closing'); d.open = true;
      a.animate([{ height: h0 + 'px', marginTop: m0, opacity: o0 }, { height: a.scrollHeight + 'px', marginTop: '-6px', opacity: 1 }], { duration: 280, easing: IO });
    }
  };
  s.addEventListener('click', click);
  track(() => { s.removeEventListener('click', click); a.getAnimations().forEach((x) => x.cancel()); d.removeAttribute('data-closing'); });
});

  })();

  (() => {
/* ─────────────────────────────────────────────────────────────
 * FOOTER  (1 is the page's footer; ?foot=2|3 keep the other two explorations)
 *
 *   1 · typed mark   the wordmark is a dot-matrix "cmx_": c, m, x type in,
 *                    the underscore is the cursor and blinks when idle
 *   2 · help screen  `cmx --help` types, the columns print as its output
 *                    line by line, an ASCII banner prints row by row,
 *                    a fresh prompt waits at the bottom
 *   3 · sign-off     "The computer that never sleeps." types in Geist
 *                    Pixel, then "never sleeps" is selected like the hero;
 *                    a tmux status line closes the page
 *
 * Plays once when the footer is a third in view. Focus inside it, or
 * reduced motion, shows the finished state. window.__foot.replay()
 * ───────────────────────────────────────────────────────────── */
(() => {
const V = 1;
const foot = root.querySelector('footer.foot');
if (!V || !foot) return;
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const marks = '<span class="mk mk-l" aria-hidden="true"></span><span class="mk mk-r" aria-hidden="true"></span>';
const COLS = [
  ['Product', 'product', [['How it works', '#how'], ['Features', '#features'], ['Agents', '#agents'], ['Pricing', '#pricing']]],
  ['Get started', 'get started', [['Guide', '/guide'], ['Create an account', '/signup'], ['Sign in', '/login']]],
];
const pr = V !== 3 ? ' class="fx-pr"' : '';
const nav = `<nav aria-label="Footer">${COLS.map(([h, m, ls]) => `<div class="fx-col"><h2${pr}>${V === 2 ? '// ' + m : h}</h2><ul>${ls.map(([t, u]) => `<li${pr}><a href="${u}">${t}</a></li>`).join('')}</ul></div>`).join('')}</nav>`;
const side = V === 2
  ? `<div class="fx-side"><p class="fx-h fx-pr">// cmx-you</p><p class="fx-pr"><span class="dot" aria-hidden="true"></span>running</p><p class="fx-pr">4 vCPU · 16 GB · 100 GB</p></div>`
  : `<div class="fx-side"><p class="fx-kick${pr ? ' fx-pr' : ''}"><span class="sl" aria-hidden="true">//</span><span class="dot" aria-hidden="true"></span>Always on<span class="sl" aria-hidden="true">//</span></p><p class="fx-copy${pr ? ' fx-pr' : ''}">© 2026 cmx · cmx.sh</p></div>`;
const top = `<div class="plate fx-top">${nav}${side}</div>`;
const ps = '<span class="fx-ps"><b>cmx-you</b> ~ $</span>';
foot.className = `foot fx fx${V}`;
foot.innerHTML = marks + (
  V === 1 ? `<div class="plate fx-term"><p class="fx-cmd">${ps} <span class="fx-t cmd">cmx --help</span></p></div>${top}<div class="plate fx-mark"><canvas class="cv-main" aria-hidden="true"></canvas><canvas class="cv-cur" aria-hidden="true"></canvas><span class="sr-only">cmx</span></div>`
: V === 2 ? `<div class="plate fx-term"><p class="fx-cmd">${ps} <span class="fx-t cmd">cmx --help</span></p><p class="fx-usage fx-pr">usage: cmx &lt;command&gt;</p></div>${top}
    <div class="plate fx-banner"><canvas aria-hidden="true"></canvas><span class="sr-only">cmx</span></div>
    <div class="plate"><div class="fx-end fx-pr"><span>${ps} <span class="fx-cur fx-blink" aria-hidden="true"></span></span><p class="fx-copy">© 2026 cmx · cmx.sh</p></div></div>`
: `${top}<div class="plate fx-sign"><p class="fx-t"><span class="ln">The computer that</span> <span class="ln"><span class="sel">never sleeps</span>.</span></p></div>
    <div class="fx-tmux" aria-hidden="true"><span class="win"><span>[cmx]</span><b>0:claude*</b><span>1:codex</span><span class="hide-xs">2:zsh</span></span><span class="st"><span><span class="dot"></span>cmx-you</span><span class="hide-xs">running</span><span class="clk"></span></span></div>`);

/* ── helpers ───────────────────────────────────────────────── */
const $ = (s, r = foot) => r.querySelector(s), $$ = (s, r = foot) => [...r.querySelectorAll(s)];
const rng = (a) => () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
// every char of el becomes a span; a zero-width .ch0 holds the cursor before the first one
function split(el) {
  const out = [], walk = (n) => [...n.childNodes].forEach((c) => {
    if (c.nodeType === 3) { const f = document.createDocumentFragment(); for (const ch of c.data) { const s = document.createElement('span'); s.className = 'ch'; s.textContent = ch; f.appendChild(s); out.push(s); } c.replaceWith(f); }
    else walk(c);
  });
  walk(el);
  const h = document.createElement('span'); h.className = 'ch0'; out[0].before(h);
  return { host: h, chars: out };
}
function cursorTo(cur, t, n) { (n ? t.chars[n - 1] : t.host).appendChild(cur); }
function show(t, n) { t.chars.forEach((c, i) => c.classList.toggle('on', i < n)); }

// Geist Pixel draws on a 26-to-the-em grid: rasterise each glyph at 10px a
// font pixel, read one sample per pixel centre, keep its lit columns. Glyphs
// then sit a fixed GAP apart (the monospace cells leave "c" adrift of "mx")
const Q = 10, GAP = 3;
function bitmap(str) {
  const F = 26 * Q, cv = document.createElement('canvas'), x = cv.getContext('2d', { willReadFrequently: true });
  const cell = 20, rows = 34, base = 24; cv.width = cell * Q * str.length; cv.height = rows * Q;
  x.font = `${F}px "Geist Pixel"`; x.fillStyle = '#000'; x.textBaseline = 'alphabetic';
  [...str].forEach((ch, i) => x.fillText(ch, (i * cell + 2) * Q, base * Q));
  const d = x.getImageData(0, 0, cv.width, cv.height).data, on = (c, r) => d[((r * Q + Q / 2) * cv.width + (c * Q + Q / 2)) * 4 + 3] > 127;
  let r0 = rows, r1 = -1;
  const gl = [...str].map((ch, i) => { let c0 = 1e9, c1 = -1; for (let r = 0; r < rows; r++) for (let c = i * cell; c < (i + 1) * cell; c++) if (on(c, r)) { c0 = Math.min(c0, c); c1 = Math.max(c1, c); r0 = Math.min(r0, r); r1 = Math.max(r1, r); } return { c0, w: c1 - c0 + 1 }; });
  let at = 0;
  gl.forEach((g) => { g.x = at; at += g.w + GAP; });
  const H = r1 - r0 + 1, lit = (i, c, r) => on(gl[i].c0 + c, r0 + r);
  // grid[r][c] → index of the glyph lit there, or -1
  const cols = at - GAP, grid = [];
  for (let r = 0; r < H; r++) { const row = new Array(cols).fill(-1); gl.forEach((g, i) => { for (let c = 0; c < g.w; c++) if (lit(i, c, r)) row[g.x + c] = i; }); grid.push(row); }
  // any other glyph, on the same baseline and rows, as its lit columns
  const glyph = (ch) => {
    const g = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    g.canvas.width = cell * Q; g.canvas.height = rows * Q; g.font = `${F}px "Geist Pixel"`; g.textBaseline = 'alphabetic'; g.fillText(ch, 2 * Q, base * Q);
    const e = g.getImageData(0, 0, g.canvas.width, g.canvas.height).data, o = (c, r) => e[((r * Q + Q / 2) * g.canvas.width + (c * Q + Q / 2)) * 4 + 3] > 127;
    let a = 1e9, b = -1; for (let r = r0; r <= r1; r++) for (let c = 0; c < cell; c++) if (o(c, r)) { a = Math.min(a, c); b = Math.max(b, c); }
    return { w: b - a + 1, lit: (c, r) => c >= 0 && c <= b - a && o(a + c, r0 + r) };
  };
  return { grid, rows: H, cols, gl, glyph };
}
const hash = (a, b) => { let h = Math.imul(a, 374761393) ^ Math.imul(b, 668265263); h = Math.imul(h ^ h >>> 13, 1274126177); return ((h ^ h >>> 16) >>> 0) / 4294967296; };
function sizeCanvas(cv, w, h) { const r = Math.min(devicePixelRatio || 1, 3); cv.width = Math.round(w * r); cv.height = Math.round(h * r); cv.style.width = w + 'px'; cv.style.height = h + 'px'; const x = cv.getContext('2d'); x.setTransform(r, 0, 0, r, 0, 0); return x; }
// terminal output: printable rows grouped by where they render, so it prints line by line at any width
const lines = () => {
  const g = [];
  $$('.fx-pr').filter((e) => !e.classList.contains('fx-end')).map((e) => [Math.round(e.getBoundingClientRect().top + scrollY), e]).sort((a, b) => a[0] - b[0])
    .forEach(([y, e]) => { const L = g[g.length - 1]; if (L && Math.abs(L.y - y) < 6) L.els.push(e); else g.push({ y, els: [e] }); });
  return g;
};
const printAll = (on) => $$('.fx-pr').forEach((e) => e.classList.toggle('on', on));
// the prompt's command, typed with its own cursor; R paces it
function prompt() {
  const cmd = split($('.cmd')), cur = document.createElement('span');
  cur.className = 'fx-cur'; cur.setAttribute('aria-hidden', 'true');
  const typed = (k, blink) => { show(cmd, k); cursorTo(cur, cmd, k); cur.classList.toggle('fx-blink', blink); cur.style.visibility = ''; };
  return {
    typed, n: cmd.chars.length, hide: () => { cur.style.visibility = 'hidden'; },
    steps: (S, R, t) => { cmd.chars.forEach((c, i) => { t += 20 + R() * 22 + (c.textContent === ' ' ? 36 : 0); S.push([t, () => typed(i + 1, false)]); }); return t; },
  };
}
const contentW = (plate) => { const cs = getComputedStyle(plate); return plate.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight); };
const INK = '11,11,12';

/* ── variants: reset() → blank, final() → finished, steps() → [[ms, fn]] ── */
let VAR;
if (V === 1) {
  const plate = $('.fx-mark'), main = $('.cv-main'), cc = $('.cv-cur');
  let B, P, n = 0, done = false;
  // one layer per canvas: its dots (layer px), a cached still image, and
  // where it sits along the mark (the cursor canvas is translated)
  const LM = { cv: main, ox: 0 }, LC = { cv: cc, ox: 0 };
  // canvases overhang the mark by M on every side (cancelled by a negative margin)
  // so dots the lens swells at the edge are not cut off
  const M = 14;
  const pitch = () => { const k = Math.max(1, Math.round(P / 5)), p = P / k; return { k, p, rad: Math.min(1.5, p * .3) }; };
  // dots: k×k per lit font pixel at ≈5px pitch, each a touch lighter or darker
  const dotsOf = (c0, c1, lit) => {
    const { k, p } = pitch(), out = [];
    for (let r = 0; r < B.rows; r++) for (let c = c0; c < c1; c++) if (lit(c, r))
      for (let i = 0; i < k; i++) for (let j = 0; j < k; j++) out.push({ x: M + (c - c0) * P + (i + .5) * p, y: M + r * P + (j + .5) * p, a: Math.round((.5 + .45 * hash(c * k + i, r * k + j)) * 20) / 20 });
    return out;
  };
  const dpr = () => Math.min(devicePixelRatio || 1, 3);
  // paint the still image: one path per alpha step
  function still(L, ds) {
    L.ds = ds;
    const b = L.base || (L.base = document.createElement('canvas')), x = b.getContext('2d'), r = dpr(), { rad } = pitch(), lv = new Map();
    b.width = L.cv.width; b.height = L.cv.height; x.setTransform(r, 0, 0, r, 0, 0);
    ds.forEach((d) => { if (!lv.has(d.a)) lv.set(d.a, []); lv.get(d.a).push(d); });
    lv.forEach((arr, a) => { x.fillStyle = `rgba(${INK},${a})`; x.beginPath(); arr.forEach((d) => { x.moveTo(d.x + rad, d.y); x.arc(d.x, d.y, rad, 0, 7); }); x.fill(); });
    blit(L);
  }
  function blit(L) { if (!L.base.width || !L.base.height) return; const x = L.cv.getContext('2d'); x.setTransform(1, 0, 0, 1, 0, 0); x.clearRect(0, 0, L.cv.width, L.cv.height); x.drawImage(L.base, 0, 0); x.setTransform(dpr(), 0, 0, dpr(), 0, 0); }
  const end = () => B.gl[2].x + B.gl[2].w;
  // the cursor sits one GAP after the last typed glyph
  const curX = () => n ? B.gl[n - 1].x + B.gl[n - 1].w + GAP : 0;
  // the letters as drawn: c-m-x, or a cipher glyph centred in a slot while it decrypts
  let shown = [0, 1, 2].map(() => null);
  const letters = () => dotsOf(0, end(), (c, r) => {
    const i = B.grid[r][c];
    for (let s = 0; s < 3; s++) { const g = B.gl[s]; if (c >= g.x && c < g.x + g.w) { const q = shown[s]; if (!q) return i === s && s < n; return s < n && q.lit(c - g.x - ((g.w - q.w) >> 1), r); } }
    return false;
  });
  function draw() {
    const H = B.rows * P;
    sizeCanvas(main, end() * P + 2 * M, H + 2 * M); main.style.margin = `${-M}px`; still(LM, letters());
    const u = B.gl[3]; sizeCanvas(cc, u.w * P + 2 * M, H + 2 * M); still(LC, dotsOf(u.x, u.x + u.w, (c, r) => B.grid[r][c] === 3));
    LC.ox = curX() * P; cc.style.transform = `translate3d(${(LC.ox - M).toFixed(2)}px,${-M}px,0)`;
  }
  const lay = () => { if (!B) B = bitmap('cmx_'); P = contentW(plate) / B.cols; egg.s = egg.v = 0; draw(); };
  const set = (k, blink) => { n = k; done = k === 3 && blink; draw(); cc.classList.toggle('fx-blink', blink); cc.style.visibility = ''; };
  // entry: `cmx --help` types at the prompt, the columns print as its output,
  // then the prompt's cursor hands over to the mark's underscore, which types c, m, x
  const pm = prompt();
  VAR = {
    lay,
    reset: () => { foot.classList.remove('fx-done'); pm.typed(0, true); printAll(false); set(0, false); cc.style.visibility = 'hidden'; },
    final: () => { pm.typed(pm.n, false); pm.hide(); printAll(true); set(3, true); foot.classList.add('fx-done'); },
    steps: () => {
      const R = rng(7), S = [[0, VAR.reset]]; let t = pm.steps(S, R, 250);
      t += 120; S.push([t, pm.hide]);
      lines().forEach((L) => { t += 18; S.push([t, () => L.els.forEach((e) => e.classList.add('on'))]); });
      t += 80; S.push([t, () => set(0, false)]);
      t += 200; S.push([t, () => set(1, false)]);
      t += 80 + R() * 40; S.push([t, () => set(2, false)]);
      t += 70 + R() * 50; S.push([t, () => set(3, false)]);
      S.push([t + 160, () => set(3, true)]);
      S.push([t + 500, () => foot.classList.add('fx-done')]);
      return S;
    },
  };

  /* ── easter egg: the lens and the decrypt ─────────────────── */
  const RAD = 96, BLUE = [21, 112, 239], INKV = [11, 11, 12], CIPHER = '#*+=<>/\\%$&01XZAS';
  const egg = { s: 0, v: 0, T: 0, x: 0, y: 0, tx: 0, ty: 0, raf: 0, last: 0, dc: null };
  // dots within RAD of the pointer swell in place and turn blue, smoothly to nothing at the edge;
  // each lit dot's own grid cell is cleared first, so the still image never doubles up
  function lens(L) {
    blit(L);
    const s = egg.s; if (Math.abs(s) < .002) return;
    const x = L.cv.getContext('2d'), lx = egg.x - L.ox, ly = egg.y, { p, rad } = pitch(), hit = [];
    for (const d of L.ds) {
      const dx = d.x - lx, dy = d.y - ly; if (dx > RAD || dx < -RAD || dy > RAD || dy < -RAD) continue;
      const q = (dx * dx + dy * dy) / (RAD * RAD); if (q < 1) hit.push([d, dx, dy, (1 - q) * (1 - q) * s]);
    }
    for (const [d] of hit) x.clearRect(d.x - p / 2, d.y - p / 2, p, p);
    for (const [d, dx, dy, e] of hit) {
      const t = Math.max(0, Math.min(1, e));
      const c = INKV.map((v, i) => Math.round(v + (BLUE[i] - v) * t)), a = d.a + (1 - d.a) * t * .7;
      x.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${a.toFixed(3)})`;
      x.beginPath(); x.arc(d.x, d.y, rad * (1 + 1.1 * t), 0, 7); x.fill();
    }
  }
  // decrypt: each letter flickers through cipher glyphs, c then m then x settle, ~600ms
  const decrypt = (now) => { egg.dc = { t0: now, tick: -1, at: [260, 420, 600] }; cc.classList.add('fx-fast'); };
  function stepDecrypt(now) {
    const D = egg.dc, t = now - D.t0, tick = Math.floor(t / 55); if (tick === D.tick) return;
    D.tick = tick;
    shown = D.at.map((a, i) => t < a ? B.glyph(CIPHER[(Math.random() * CIPHER.length) | 0]) : null);
    if (t >= D.at[2]) { egg.dc = null; shown = [null, null, null]; cc.classList.remove('fx-fast'); }
    still(LM, letters());
  }
  function frame(now) {
    const dt = Math.min(48, now - (egg.last || now)) / 1000; egg.last = now;
    // a lightly underdamped spring for strength, so the dots settle back with a small give
    egg.v += (170 * (egg.T - egg.s) - 18 * egg.v) * dt; egg.s += egg.v * dt;
    const k = 1 - Math.exp(-dt / .06); egg.x += (egg.tx - egg.x) * k; egg.y += (egg.ty - egg.y) * k;
    if (egg.dc) stepDecrypt(now);
    const rest = !egg.dc && Math.abs(egg.T - egg.s) < .002 && Math.abs(egg.v) < .002 && Math.abs(egg.tx - egg.x) < .2 && Math.abs(egg.ty - egg.y) < .2;
    if (rest && egg.T === 0) { egg.s = egg.v = 0; blit(LM); blit(LC); egg.raf = 0; egg.last = 0; return; }
    lens(LM); lens(LC);
    egg.raf = rest ? 0 : requestAnimationFrame(frame);
    if (!egg.raf) egg.last = 0;
  }
  const wake = () => { if (!egg.raf) egg.raf = requestAnimationFrame(frame); };
  // pointer in mark coords, and whether it is over the mark (its box, 16px slack)
  const at = (e) => { const r = main.getBoundingClientRect(), W = (B.cols) * P; const x = e.clientX - r.left, y = e.clientY - r.top; return { x, y, in: x > M - 16 && x < M + W + 16 && y > M - 16 && y < M + B.rows * P + 16 }; };
  let inside = false;
  // hover lights the dots under the pointer; a click (or a tap) decrypts the letters
  function enter(q) {
    inside = true; egg.T = 1; plate.style.cursor = 'pointer';
    if (Math.abs(egg.s) < .05) { egg.x = q.x; egg.y = q.y; }
  }
  function leave() { inside = false; egg.T = 0; plate.style.cursor = ''; wake(); }
  if (!reduce) {
    plate.addEventListener('pointermove', (e) => {
      if (!done || (e.pointerType === 'touch' && !inside)) return;
      const q = at(e); egg.tx = q.x; egg.ty = q.y;
      if (q.in && !inside) enter(q); else if (!q.in && inside) leave();
      if (inside) wake();
    });
    plate.addEventListener('pointerdown', (e) => {
      if (!done) return;
      const q = at(e); if (!q.in) return;
      egg.tx = q.x; egg.ty = q.y; enter(q);
      if (!egg.dc) decrypt(performance.now());
      wake();
    });
    ['pointerleave', 'pointercancel'].forEach((t) => plate.addEventListener(t, () => { if (inside) leave(); }));
    plate.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse' && inside) leave(); });
  }
  window.__egg = { egg, at: (x, y) => { const r = main.getBoundingClientRect(); return { clientX: r.left + M + x, clientY: r.top + M + y }; } };
}
if (V === 2) {
  const pm = prompt(), cv = $('.fx-banner canvas'), plate = $('.fx-banner');
  let B, P, rowsShown = 0;
  const GLYPH = '@';
  function draw() {
    const W = B.cols * P, H = B.rows * P, x = sizeCanvas(cv, W, H);
    x.textAlign = 'center'; x.textBaseline = 'middle'; x.font = `500 ${Math.round(P * 1.3)}px "Geist Mono"`;
    x.fillStyle = `rgba(${INK},.82)`;
    for (let r = 0; r < Math.min(rowsShown, B.rows); r++) for (let c = 0; c < B.cols; c++) if (B.grid[r][c] >= 0) {
      if (P < 16) x.fillRect(c * P + P * .14, r * P + P * .14, P * .72, P * .72);
      else x.fillText(GLYPH[(c + r) % GLYPH.length], (c + .5) * P, (r + .5) * P + P * .06);
    }
  }
  const lay = () => { if (!B) B = bitmap('cmx_'); P = contentW(plate) / B.cols; draw(); };
  const banner = (k) => { rowsShown = k; draw(); };
  VAR = {
    lay,
    reset: () => { pm.typed(0, true); printAll(false); banner(0); },
    final: () => { pm.typed(pm.n, false); pm.hide(); printAll(true); banner(99); },
    steps: () => {
      const R = rng(11), S = [[0, VAR.reset]]; let t = 700;
      t = pm.steps(S, R, t);
      t += 260; S.push([t, pm.hide]);
      lines().forEach((L) => { t += 40; S.push([t, () => L.els.forEach((e) => e.classList.add('on'))]); });
      for (let r = 1; r <= B.rows; r++) { t += 24; S.push([t, () => banner(r)]); }
      t += 120; S.push([t, () => $('.fx-end').classList.add('on')]);
      return S;
    },
  };
}
if (V === 3) {
  const tx = split($('.fx-sign p')), cur = document.createElement('span'), sel = $('.sel');
  cur.className = 'fx-cur'; cur.setAttribute('aria-hidden', 'true');
  const type = (k, blink) => { show(tx, k); cursorTo(cur, tx, k); cur.classList.toggle('fx-blink', blink); };
  const select = (on) => sel.classList.toggle('on-sel', on);
  const clk = $('.clk'), tick = () => { const d = new Date(); clk.textContent = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); };
  tick(); setInterval(tick, 15000);
  VAR = {
    lay: () => {},
    reset: () => { type(0, true); select(false); },
    final: () => { type(tx.chars.length, true); select(true); },
    steps: () => {
      const R = rng(3), S = [[0, VAR.reset]]; let t = 800;
      tx.chars.forEach((c, i) => {
        const p = tx.chars[i - 1]?.textContent;
        t += 44 + R() * 46 + (p === ' ' ? 40 + R() * 90 : 0);
        S.push([t, () => type(i + 1, false)]);
      });
      S.push([t + 240, () => type(tx.chars.length, true)]);
      S.push([t + 720, () => select(true)]);
      return S;
    },
  };
}

/* ── engine ────────────────────────────────────────────────── */
let timers = [], played = false;
const stop = () => { timers.forEach(clearTimeout); timers = []; };
const finish = () => { stop(); played = true; VAR.final(); };
const replay = () => { stop(); played = true; if (reduce) return VAR.final(); VAR.steps().forEach(([ms, fn]) => timers.push(setTimeout(fn, ms))); };
const ready = (document.fonts ? Promise.all([document.fonts.load('40px "Geist Pixel"'), document.fonts.load('500 20px "Geist Mono"')]) : Promise.resolve()).catch(() => {});
ready.then(() => {
  VAR.lay();
  if (reduce) return finish();
  VAR.reset();
  foot.addEventListener('focusin', () => { if (timers.length || !played) finish(); });
  const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && !played) { io.disconnect(); replay(); } }, { threshold: .3 });
  io.observe(foot);
  let q = 0; addEventListener('resize', () => { cancelAnimationFrame(q); q = requestAnimationFrame(VAR.lay); });
});
})();
  })();

  return () => { alive = false; bin.forEach((fn) => fn()); bin.clear(); };
}
